import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, GraduationCap, Search, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { useSettings } from "../lib/SettingsContext";

const NAV = [
  { to: "/", label: "Beranda" },
  { to: "/tentang-kami", label: "Tentang Kami" },
  { to: "/akademik", label: "Akademik" },
  { to: "/kehidupan-sekolah", label: "Kehidupan Sekolah" },
  { to: "/prestasi", label: "Prestasi" },
  { to: "/berita", label: "Berita" },
  { to: "/ppdb", label: "PPDB" },
  { to: "/galeri", label: "Galeri" },
  { to: "/kontak", label: "Kontak" },
];

export default function Navbar() {
  const { settings } = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const transparent = isHome && !scrolled;
  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent"
          : "bg-white/90 backdrop-blur-xl border-b border-slate-200/70 shadow-sm"
      }`}
    >
      {/* Top strip */}
      <div className={`hidden lg:block transition-all ${transparent ? "border-b border-white/15" : "border-b border-slate-200/60"}`}>
        <div className="max-w-7xl mx-auto px-6 py-1.5 flex items-center justify-between text-xs">
          <span className={transparent ? "text-amber-200" : "text-slate-500"}>
            Akreditasi A • NPSN 60301717 • Yayasan Pendidikan Kristen
          </span>
          <div className="flex items-center gap-4">
            <Link to="/portal/murid" data-testid="nav-portal-murid" className={`${transparent ? "text-white/90" : "text-slate-600"} hover:text-amber-500`}>Portal Murid</Link>
            <span className={transparent ? "text-white/30" : "text-slate-300"}>|</span>
            <Link to="/portal/guru" data-testid="nav-portal-guru" className={`${transparent ? "text-white/90" : "text-slate-600"} hover:text-amber-500`}>Portal Guru</Link>
            <span className={transparent ? "text-white/30" : "text-slate-300"}>|</span>
            <Link to="/admin" data-testid="nav-admin-link" className={`${transparent ? "text-amber-300" : "text-amber-600"} font-semibold hover:underline`}>Admin</Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-3 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl overflow-hidden ${
            settings?.logo_url
              ? (transparent ? "bg-white/95 backdrop-blur ring-2 ring-amber-400/50 p-1" : "bg-white ring-1 ring-slate-200 p-1")
              : (transparent ? "bg-white/15 backdrop-blur border border-white/25" : "bg-[#0B1D3A]")
          } transition-colors`}>
            {settings?.logo_url ? (
              <img src={settings.logo_url} alt="SMAS YPK 2 BIAK" className="w-full h-full object-contain" />
            ) : (
              <GraduationCap className="w-7 h-7 text-amber-400" strokeWidth={1.5} />
            )}
          </div>
          <div>
            <div className={`font-serif font-bold text-base leading-tight ${transparent ? "text-white" : "text-[#0B1D3A]"}`}>
              SMAS YPK 2
            </div>
            <div className={`text-[11px] tracking-[0.18em] font-semibold ${transparent ? "text-amber-300" : "text-amber-600"}`}>
              BIAK — PAPUA
            </div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-lg text-[13px] font-semibold uppercase tracking-wider transition-colors ${
                  isActive
                    ? transparent ? "text-amber-300" : "text-amber-600"
                    : transparent ? "text-white/90 hover:text-amber-300" : "text-slate-700 hover:text-[#0B1D3A]"
                }`
              }
              end={item.to === "/"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/ppdb" data-testid="nav-cta-ppdb" className="hidden sm:inline-flex">
            <Button className="bg-amber-500 hover:bg-amber-600 text-[#0B1D3A] font-bold rounded-full px-5 shadow-md shadow-amber-500/20">
              Daftar Sekarang
            </Button>
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="xl:hidden p-2 rounded-lg"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? (
              <X className={transparent ? "text-white" : "text-[#0B1D3A]"} />
            ) : (
              <Menu className={transparent ? "text-white" : "text-[#0B1D3A]"} />
            )}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden bg-white border-t border-slate-200 shadow-xl">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-lg text-sm font-semibold ${
                    isActive ? "bg-[#0B1D3A] text-amber-300" : "text-slate-700 hover:bg-slate-100"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-3 border-t border-slate-200 mt-2 flex flex-col gap-2">
              <Link to="/portal/murid" className="px-4 py-2 text-sm text-slate-600">Portal Murid</Link>
              <Link to="/portal/guru" className="px-4 py-2 text-sm text-slate-600">Portal Guru</Link>
              <Link to="/admin" className="px-4 py-2 text-sm font-semibold text-amber-600">Admin Dashboard</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
